import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AppState, AppErrorMessageEvent, AppSuccessMessageEvent, AppMessageEvent, AppInfoMessageEvent } from '../core'
import { Observable, Subscription, timer } from 'rxjs';
import { AuthenticationService, LookupService } from '../core/services';
import { Router } from '@angular/router';
import { AppConfig } from '../../environments/environment';
import { buildInfo } from '../../build'
import { TestbedRxdbState } from '../core/services/testbed-rxdb';
import { DialerInfo, DialerService } from 'dialer';
import { User } from '../core/models';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ErrorModalComponent } from '../shared/components/error-modal/error-modal.component';

@Component({
  selector: 'app-status-bar',
  templateUrl: './status-bar.component.html',
  styleUrls: ['./status-bar.component.scss']
})
export class StatusBarComponent implements OnInit, OnDestroy {

  @Input() hasUnsavedDataFn : () => Observable<boolean> | boolean

  errorEvents : AppErrorMessageEvent[] = [] // display most recent error in this array
  successEvents : AppSuccessMessageEvent[] = [];
  infoEvents : AppInfoMessageEvent[] = [];

  MAX_SUCCESS_CNT = 3
  MAX_ERROR_CNT = 7
  MAX_INFO_CNT = 3

  isElectron : boolean = false
  env : string = ''
  clientBuildInfo = buildInfo
  latestClientVersion = ''
  clientVersionMismatch = false
  acceptDialerServerVersionsRegExStr = ".*"
  dialerVersionOk = true

  loginExpired = false
  loginExpiring = false
  loginExpireMsg = ''
  compatibilitySubscription : Subscription
  user: User

  constructor(public appState: AppState,
    private authenticationService : AuthenticationService,
    private router: Router,
    public rxdbState : TestbedRxdbState,
    private lookupService: LookupService,
    public dialerService: DialerService,
    public bsModalService: BsModalService ) {
    appState.errorEmitter.subscribe(this.displayError)
    appState.successEmitter.subscribe(this.displaySuccess)
    appState.infoEmitter.subscribe(this.displayInfo)
    this.isElectron = appState.isElectron()
    this.env = AppConfig.environment
  }

  userSubscription : Subscription

  ngOnInit(): void {
    this.appState.doWhenReady( () => {
      this.compatibilitySubscription = this.lookupService.rxCollection.findOne('compatibility').$.subscribe( compatibility => {
        this.latestClientVersion = compatibility?.values?.find(value=>value.id=="webClient")?.latestVersion
        this.acceptDialerServerVersionsRegExStr = compatibility?.values?.find(value=>value.id=="dialerServer")?.acceptVersionsRegEx || '.*'
        this.checkDialerServerVersion(this.dialerService.dialerInfo)
        const wasMismatchedVersion = this.clientVersionMismatch
        this.clientVersionMismatch = this.latestClientVersion && this.clientBuildInfo.version!=this.latestClientVersion
        if ( this.clientVersionMismatch ) {
          let msg = ''
          if ( !this.appState.isElectron() ) {
            msg = '(ctrl-F5 to refresh browser with latest)'
          }
          msg = `Installed version is ${this.clientBuildInfo.version} but expected ${this.latestClientVersion} ${msg}`
          this.appState.notifyError(msg,this,60)
        } else if ( wasMismatchedVersion ) {
          this.appState.notifySuccess(`Version ${this.clientBuildInfo.version}`,this,3)
        }
      })
      this.dialerService.dialerInfo$.subscribe( dialerInfo => {
        this.checkDialerServerVersion(dialerInfo)
      })
      let checkLoginExpirationSubscription : Subscription
      this.userSubscription = this.appState.userSubject.subscribe(user => {
        this.user = user
        if ( !user ) {
          checkLoginExpirationSubscription?.unsubscribe()
        } else {
          checkLoginExpirationSubscription?.unsubscribe()
          checkLoginExpirationSubscription = timer(0,60*60*1000).subscribe(_=>{
            let expDate = user?.expireDate
            if ( expDate && !(expDate instanceof Date) )  {
              expDate = new Date(expDate)
            }
            if ( expDate ) {
              let expireWarnDate = new Date(expDate.getTime())
              expireWarnDate.setDate(expDate.getDate() - 7)
              const now = new Date()
              this.loginExpired = now > expDate
              this.loginExpiring = !this.loginExpired && now > expireWarnDate
              this.loginExpireMsg = 'Login expiration: ' + expDate.toString()
              //console.log( 'expireDate: ' + expDate.toISOString() )
              //console.log( 'expireWarnDate: ' + expireWarnDate.toISOString() )
            } else {
              // should never get here
              this.loginExpired = this.loginExpiring = false
            }
          })
        }
      })
    })
  }

  ngOnDestroy(): void {
    this.compatibilitySubscription?.unsubscribe()
    this.userSubscription?.unsubscribe()
  }


  checkDialerServerVersion( dialerInfo: DialerInfo ) {
    if ( dialerInfo ) {
      const fullVersion = dialerInfo.version + '.' + dialerInfo.git
      this.dialerVersionOk = new RegExp(this.acceptDialerServerVersionsRegExStr).test(fullVersion)
    } else {
      this.dialerVersionOk = true // just be optimistic :-)
    }
  }

  logout() {
    let hasUnsavedData = this.hasUnsavedDataFn && this.hasUnsavedDataFn()

    const logoutAction = () => {
      this.authenticationService.logout();
      this.router.navigate(['/login']);
    }

    if ( hasUnsavedData === false ) {
      logoutAction()
    } else {
      (hasUnsavedData as Observable<boolean>).subscribe( bHasUnsavedData => {
        if ( !bHasUnsavedData ) {
          logoutAction()
        }
      })
    }
  }

  lastErrorEvent : AppErrorMessageEvent
  displayError = ( event : AppErrorMessageEvent ) => {
    const updateLastFn = () => this.lastErrorEvent = this.last(this.errorEvents);
    this.displayEvent(event,this.errorEvents,this.MAX_ERROR_CNT)
    this.scheduleEventRemoval(event,this.errorEvents,updateLastFn);
    updateLastFn();
  }

  lastSuccessEvent : AppSuccessMessageEvent
  displaySuccess = ( event : AppSuccessMessageEvent ) => {
    const updateLastFn = () => this.lastSuccessEvent = this.last(this.successEvents);
    this.displayEvent(event,this.successEvents,this.MAX_SUCCESS_CNT)
    // assume we can remove all errors from a source if we get success
    this.filter(this.errorEvents, evt => evt.source != event.source)
    this.scheduleEventRemoval(event,this.successEvents,updateLastFn);
    updateLastFn();
  }

  lastInfoEvent : AppInfoMessageEvent
  displayInfo = ( event : AppInfoMessageEvent ) => {
    // only one info msg per event source (allow one from task list, one from task log, etc...)
    this.filter( this.infoEvents, evt => evt.source != event.source )
    const updateLastFn = () => this.lastInfoEvent = this.last(this.infoEvents);
    this.displayEvent(event,this.infoEvents,this.MAX_INFO_CNT)
    this.scheduleEventRemoval(event,this.infoEvents,updateLastFn);
    updateLastFn()
  }

  displayEvent = ( event : AppMessageEvent, events : AppMessageEvent[], maxEvents: number) => {
    if ( event?.message ) {
      events.push(event)
    } else {
      // empty event implies remove all messages for that source
      this.filter( events, evt => evt.source != event.source )
    }
    if ( events.length > maxEvents ) {
      events.shift()
    }
  }

  // remove things but don't reassign array
  private filter( events : AppMessageEvent[],  filterPred : (evt:AppMessageEvent) => boolean ) {
    let filtered = events.filter(filterPred)
    events.length = 0
    events.push(...filtered)
  }

  private removeEvent( event : AppMessageEvent, events : AppMessageEvent[], cleanupFn: ()=>void ) {
    let index = events.findIndex( evt => evt == event)
    if ( index >= 0 ) {
      events.splice(index,1)
    }
    cleanupFn?.();
  }

  private scheduleEventRemoval = ( event: AppMessageEvent, events: AppMessageEvent[], cleanupFn: ()=>void ) => {
    if ( event?.message && event.expireSeconds ) {
      timer( event.expireSeconds*1000 ).subscribe( ms => {
        this.removeEvent(event, events,cleanupFn);
      })
    }
  }

  last<EventType>( events : EventType[] ) {
    return events.length ? events[events.length-1] : null
  }

  lastMsg( events : AppMessageEvent[] ) {
    let msg = this.last(events)?.message
    if ( msg?.length > 80 ) {
      msg = msg.slice(0,80) + '...'
    }
    return msg
  }

  showEvent( event : AppMessageEvent ) {
    if ( event instanceof AppErrorMessageEvent ) {
      const errorEvent : AppErrorMessageEvent = event
      const context : ModalOptions = {
        initialState: {
          errorEvents: this.errorEvents,
          errorEvent: errorEvent
        },
        class: 'modal-lg',
        id: ErrorModalComponent.ID,
        animated: true
      }
      const modalRef = this.bsModalService.show(ErrorModalComponent, context );
      const deleteSub = modalRef.content?.errorDeleted$.subscribe( e => {
        const updateLastFn = ()=>this.lastErrorEvent = this.last(this.errorEvents);
        this.removeEvent(e,this.errorEvents, updateLastFn);
        updateLastFn();
      })
      modalRef.onHide.subscribe( () => {
        if ( !this.errorEvents.length ) {
          this.lastErrorEvent = null
        }
        deleteSub?.unsubscribe()
      })
    } else {
      alert( event.type + ' - ' + event.message )
    }
  }
}
